import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import {
  bootstrapAuth,
  selectAuthPhase,
  setPhase,
  tokenRefreshed,
  type AuthPhase,
} from '@/pages/auth/authSlice';
import { getAuthService } from '@/services/authService';

/**
 * Dispatches the OIDC bootstrap thunk on protected routes.
 * Also subscribes to UserManager.events.addUserLoaded to keep
 * Redux state in sync on silent token refresh.
 */
export function useOidcBootstrap({ skip }: { skip: boolean }): { phase: AuthPhase } {
  const dispatch = useAppDispatch();
  const phase = useAppSelector(selectAuthPhase);

  useEffect(() => {
    if (skip) return;

    if (phase === 'idle') {
      dispatch(bootstrapAuth());
    }
  }, [skip, phase]);

  // Sync token on silent refresh (runs once per app lifetime via singleton)
  useEffect(() => {
    const authService = getAuthService();
    const handler = (user: any) => {
      if (user?.access_token) {
        dispatch(tokenRefreshed(user.access_token));
      }
    };
    authService.UserManager.events.addUserLoaded(handler);
    return () => {
      authService.UserManager.events.removeUserLoaded(handler);
    };
  }, []);

  return { phase };
}
